import { pdf } from '@react-pdf/renderer'
import * as Sentry from '@sentry/react'
import { dashboard20240730Client } from '@sequencehq/api/dist/clients/dashboard/v20240730'
import { InvoiceModel } from '@sequencehq/core-models'
import { ReactPdfInvoice } from '@sequencehq/invoice-content'
import { getPortalDomain, registerReactPdfFonts } from '@sequencehq/utils'
import { useFetchDataForLocalPdfInvoice } from 'InvoiceEditor/components/Header/useFetchDataForLocalPdfInvoice'
import { createSubAccountUsageBreakdownFromApiResponse } from 'InvoiceEditor/domainManagement/invoiceEditorAdapter'
import { useInvoiceEditorContext } from 'InvoiceEditor/hooks/useInvoiceEditorContext'
import { getEnvironment, isProductionEnv } from 'lib/environment/environment'
import exhaustiveGuard from 'lib/exhaustiveGuard/exhaustiveGuard'
import { useCallback, useEffect, useState } from 'react'

type UseInvoicePdfPreview = (props: { isOpen: boolean }) => {
  previewData: Blob | undefined
}

registerReactPdfFonts()

export const useInvoicePdfPreview: UseInvoicePdfPreview = props => {
  const { data } = useInvoiceEditorContext()
  const { fetchData } = useFetchDataForLocalPdfInvoice()

  const portalDomain = getPortalDomain(getEnvironment())

  const [previewData, setPreviewData] = useState<Blob | undefined>()

  /**
   * Main functions
   */
  const localPreviewPdf = useCallback(
    async ({
      customerId,
      invoiceId
    }: {
      customerId: InvoiceModel['customerId']
      invoiceId: InvoiceModel['id']
    }) => {
      try {
        const pdfInvoiceData = await fetchData({ invoiceId, customerId })

        const invoiceProps = {
          invoice: {
            ...pdfInvoiceData.invoice,
            customerPortalEnabled:
              pdfInvoiceData.merchant.customerPortalEnabled,
            creditBalances: pdfInvoiceData.creditBalances
          },
          lineItems: pdfInvoiceData.lineItems,
          lineItemGroups: pdfInvoiceData.lineItemGroups,
          merchantDetails: pdfInvoiceData.merchantDetails,
          creditBalances: pdfInvoiceData.creditBalances,
          showNonProdWatermark: !isProductionEnv(),
          urlRootApi: import.meta.env.VITE_API_ORIGIN,
          urlRootCustomerPortal: portalDomain || '',
          defaultDueDateDays: pdfInvoiceData.merchant.defaultDueDateDays,
          isCustomerPortalEnabled:
            pdfInvoiceData.merchant.customerPortalEnabled,
          isRenderedInBrowser: true,
          subAccountUsageBreakdown:
            createSubAccountUsageBreakdownFromApiResponse(
              pdfInvoiceData.subAccountUsageBreakdown
            )
        }

        /**
         * NOTE: see apps/dashboard-web/src/components/InvoiceEditor/components/Header/useInvoicePdfDownload.tsx
         */
        let paymentDetailsPageNumber: number | undefined = undefined

        const setPaymentDetailsPageNumber = (pageNumber: number) => {
          paymentDetailsPageNumber = pageNumber
        }

        await pdf(
          <ReactPdfInvoice
            {...invoiceProps}
            setExternalPaymentDetailsPageNumber={setPaymentDetailsPageNumber}
          />
        ).toBlob()

        const blob = await pdf(
          <ReactPdfInvoice
            {...invoiceProps}
            externalPaymentDetailsPageNumber={paymentDetailsPageNumber}
          />
        ).toBlob()

        setPreviewData(blob)
      } catch (e) {
        Sentry.captureException(e)
      }
    },
    [fetchData, portalDomain]
  )

  const remotePreviewPdf = useCallback(
    async ({ invoiceId }: { invoiceId: InvoiceModel['id'] }) => {
      try {
        const invoicePdfRes = await dashboard20240730Client.getInvoicePdf({
          id: invoiceId
        })

        const blob = invoicePdfRes.data

        if (!blob) {
          throw new Error('An error occurred while downloading the invoice')
        }

        setPreviewData(blob)
      } catch (e) {
        Sentry.captureException(e)
      }
    },
    []
  )

  /**
   * On load
   */
  useEffect(() => {
    if (!props.isOpen || !data.invoice.id || !data.invoice.customerId) {
      return
    }

    switch (data.invoice.status) {
      case 'DRAFT':
      case 'IN_PROGRESS':
      case 'FINAL':
        void localPreviewPdf({
          invoiceId: data.invoice.id,
          customerId: data.invoice.customerId
        })
        break
      case 'SENT':
      case 'VOIDED':
        void remotePreviewPdf({ invoiceId: data.invoice.id })
        break
      default:
        return exhaustiveGuard(data.invoice.status)
    }
  }, [
    data.invoice.customerId,
    data.invoice.id,
    data.invoice.status,
    localPreviewPdf,
    props.isOpen,
    remotePreviewPdf
  ])

  return {
    previewData
  }
}
